import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const heading = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const MAX_ATTEMPTS = 6;

export default function EticketSubscriptionSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | pending | error
  const [planLabel, setPlanLabel] = useState('');

  const PLAN_NAMES = {
    eticket: 'StayMii E-Ticket',
    eticket_einvoice: 'StayMii E-Ticket + E-Invoice',
    eticket_sgmc: 'StayMii E-Ticket + SGMC',
    full_suite: 'StayMii Full Suite',
    custom: 'StayMii E-Ticket — Custom Plan',
  };

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let cancelled = false;
    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/api/eticket/subscription/status/${sessionId}`);
        const ps = res.data?.payment_status;
        const name = PLAN_NAMES[res.data?.plan_id] || 'StayMii E-Ticket';
        const amt = res.data?.amount;
        setPlanLabel(amt ? `${name} (RM${Number(amt).toLocaleString('en-MY')}/month)` : name);
        if (ps === 'paid') { setState('paid'); return; }
        if (res.data?.status === 'expired') { setState('error'); return; }
        if (attempt >= MAX_ATTEMPTS) { setState('pending'); return; }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        if (attempt >= MAX_ATTEMPTS) { setState('error'); return; }
        setTimeout(() => poll(attempt + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="eticket-sub-success-page">
      <LandingNavBar />
      <div className="flex-1 flex items-center justify-center p-6 pt-28">
        <div className="w-full max-w-md text-center">
          {state === 'checking' && (
            <>
              <div className="mx-auto w-16 h-16 bg-cyan-50 rounded-2xl flex items-center justify-center mb-6">
                <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading}>Confirming your subscription…</h2>
              <p className="text-slate-500">This only takes a moment.</p>
            </>
          )}
          {state === 'paid' && (
            <>
              <div className="mx-auto w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading} data-testid="sub-success">You're subscribed! 🎉</h2>
              <p className="text-slate-500 mb-8 leading-relaxed">Your {planLabel || 'StayMii E-Ticket'} plan is active. Create your account or log in to start selling tickets.</p>
              <Link to="/auth/signup?category=eticket" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl font-bold text-sm" data-testid="sub-goto-signup">
                Set up my account <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
          {state === 'pending' && (
            <>
              <div className="mx-auto w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-6">
                <Loader2 className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading}>Payment is processing</h2>
              <p className="text-slate-500 mb-6">We're still confirming your payment. You'll receive an email once it's complete.</p>
              <Link to="/e-ticket" className="text-cyan-700 underline underline-offset-2 text-sm">Back to e-Ticket</Link>
            </>
          )}
          {state === 'error' && (
            <>
              <div className="mx-auto w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading} data-testid="sub-error">Something went wrong</h2>
              <p className="text-slate-500 mb-6">We couldn't confirm this subscription. If you were charged, please contact support.</p>
              <Link to="/e-ticket" className="text-cyan-700 underline underline-offset-2 text-sm">Back to e-Ticket</Link>
            </>
          )}
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}
